import { Button } from '@amore/ui/components/button'
import { BedDouble, CalendarPlus, Car, Check, X } from 'lucide-react'
import { formatDate } from '@/lib/format'
import { dietLabels } from '@/lib/labels'
import { useWedding } from '@/mock/store'
import type { Party, WebReply } from '@/mock/types'
import { useToast } from '@/shell/toaster'

/**
 * B3: after sending. A warm thank-you that matches the answer (coming or not),
 * a summary of what was sent so the guest can check it, and what's next:
 * the calendar for those coming, and how to reach the couple if plans change
 * (changing a reply online is #201).
 */
export function ThankYou({
  party,
  reply,
  onHome,
}: {
  party: Party
  reply: WebReply
  onHome: () => void
}) {
  const data = useWedding()
  const { showToast } = useToast()
  const coming = reply.answers.filter((answer) => answer.attendance === 'confirmed')
  const parts = data.dayParts.filter((part) => party.parts.includes(part.id))
  const date = data.identity.date
  const needs = [
    reply.accommodation && { icon: BedDouble, label: 'ubytování' },
    reply.transport && { icon: Car, label: 'odvoz' },
  ].filter((item) => item !== false)

  return (
    <div className="space-y-10 pt-16 pb-10">
      <header className="space-y-4 text-center">
        <span className="mx-auto grid size-14 place-items-center rounded-full bg-success text-success-foreground">
          <Check aria-hidden="true" className="size-7" strokeWidth={2.5} />
        </span>
        <h1 className="font-heading text-4xl leading-tight">
          {coming.length ? 'Děkujeme, těšíme se na vás!' : 'Děkujeme za odpověď'}
        </h1>
        <p className="mx-auto max-w-sm text-muted-foreground">
          {coming.length
            ? `Vaši odpověď už máme.${date ? ` Uvidíme se ${formatDate(date, 'long')}.` : ''}`
            : 'Moc nás mrzí, že nedorazíte. Budeme na vás myslet.'}
        </p>
      </header>

      <section
        aria-label="Co jste poslali"
        className="space-y-5 rounded-[1.75rem] border border-border bg-card p-6"
      >
        <h2 className="text-sm font-semibold">Co jste poslali</h2>
        <ul className="space-y-3">
          {reply.answers.map((answer) => {
            const yes = answer.attendance === 'confirmed'
            return (
              <li key={answer.personId} className="flex items-start gap-3">
                <span
                  className={
                    yes
                      ? 'grid size-6 shrink-0 place-items-center rounded-full bg-success text-success-foreground'
                      : 'grid size-6 shrink-0 place-items-center rounded-full bg-danger text-danger-foreground'
                  }
                >
                  {yes ? (
                    <Check aria-hidden="true" className="size-3.5" strokeWidth={2.5} />
                  ) : (
                    <X aria-hidden="true" className="size-3.5" strokeWidth={2.5} />
                  )}
                </span>
                <span className="min-w-0">
                  <span className="block font-medium">
                    {`${answer.firstName} ${answer.lastName}`.trim() || 'Doprovod'}
                  </span>
                  <span className="block text-sm text-muted-foreground">
                    {[
                      yes ? 'přijde' : 'nepřijde',
                      ...answer.diets.map((diet) => dietLabels[diet].toLowerCase()),
                    ].join(' · ')}
                  </span>
                </span>
              </li>
            )
          })}
        </ul>
        {needs.length ? (
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span className="text-muted-foreground">Potřebujete</span>
            {needs.map(({ icon: Icon, label }) => (
              <span key={label} className="inline-flex items-center gap-1.5">
                <Icon aria-hidden="true" className="size-4 text-muted-foreground" />
                {label}
              </span>
            ))}
          </p>
        ) : null}
        {reply.message ? (
          <p className="text-[15px] leading-relaxed text-foreground/85">„{reply.message}“</p>
        ) : null}
      </section>

      {coming.length && parts.length ? (
        <section className="space-y-3 text-center">
          <h2 className="font-semibold">Na co se můžete těšit</h2>
          <ul className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-muted-foreground">
            {parts.map((part) => (
              <li key={part.id}>
                <span className="text-foreground">{part.label}</span>
                {part.time ? ` ${part.time}` : ''}
              </li>
            ))}
          </ul>
          {date ? (
            <Button
              appearance="outline"
              tone="neutral"
              onClick={() =>
                showToast({
                  title: 'Uložili bychom do kalendáře',
                  description: 'V prototypu jen na oko.',
                })
              }
            >
              <CalendarPlus aria-hidden="true" className="size-4" />
              Přidat do kalendáře
            </Button>
          ) : null}
        </section>
      ) : null}

      <div className="space-y-3 text-center">
        <p className="text-sm text-muted-foreground">
          Kdyby se něco změnilo, ozvěte se nám prosím.
        </p>
        <Button appearance="ghost" tone="neutral" onClick={onHome}>
          Zpět na svatební web
        </Button>
      </div>
    </div>
  )
}
